import { useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Receipt, Printer, Calendar, User, CreditCard, X } from 'lucide-react';
import { useOrderStore, useSettingsStore } from '../../hooks';
import { Button } from '@lancar-pos/ui';
import { formatCurrency } from '@lancar-pos/shared';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { ReceiptContent, usePrintReceipt } from '../receipt/ReceiptPrint';

export function OrderDetailPage() {
  const navigate = useNavigate();
  const { orderId } = useParams({ from: '/app/inventory/orders/$orderId' });
  const { getOrderById } = useOrderStore();
  const { getSetting } = useSettingsStore();

  const order = useMemo(() => {
    return getOrderById(orderId);
  }, [orderId, getOrderById]);

  const businessName = getSetting<string>('business_name') || 'LancarPOS';
  const businessAddress = getSetting<string>('business_address') || '';
  const businessPhone = getSetting<string>('business_phone') || '';
  const [showReceipt, setShowReceipt] = useState(false);
  const { handlePrint } = usePrintReceipt();

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Pesanan Tidak Ditemukan</h2>
          <p className="text-gray-500 mb-4">Pesanan dengan ID tersebut tidak ada.</p>
          <Button onClick={() => navigate({ to: '/app/inventory/orders' })}>
            Kembali ke Riwayat
          </Button>
        </div>
      </div>
    );
  }

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'cash':
        return 'Tunai';
      case 'card':
        return 'Kartu';
      case 'qris':
        return 'QRIS';
      default:
        return method;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Selesai';
      case 'cancelled':
        return 'Dibatalkan';
      case 'pending':
        return 'Menunggu';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate({ to: '/app/inventory/orders' })}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Detail Pesanan</h1>
              <p className="text-sm text-gray-500">{order.orderNumber}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 lg:p-6 space-y-4">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="text-center border-b border-gray-200 pb-4 mb-4">
            <h2 className="text-lg font-bold text-gray-900">{businessName}</h2>
            {businessAddress && <p className="text-sm text-gray-500">{businessAddress}</p>}
            <div className="mt-2">
              <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(order.status)}`}>
                {getStatusLabel(order.status)}
              </span>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4" />
              <span>{format(new Date(order.createdAt), 'EEEE, dd MMMM yyyy', { locale: id })}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4" />
              <span>{format(new Date(order.createdAt), 'HH:mm:ss')}</span>
            </div>
            {order.customerId && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <User className="w-4 h-4" />
                <span>Pelanggan: {order.customerId}</span>
              </div>
            )}
          </div>

          <div className="border-t border-b border-gray-200 py-4 mb-4">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="pb-2">Produk</th>
                  <th className="pb-2 text-center">Qty</th>
                  <th className="pb-2 text-right">Harga</th>
                  <th className="pb-2 text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {order.items.map((item, index) => (
                  <tr key={index}>
                    <td className="py-2">
                      <div>
                        <p className="font-medium text-gray-900">{item.productName}</p>
                        {item.variant && (
                          <p className="text-xs text-gray-500">{item.variant.name}</p>
                        )}
                        {item.notes && (
                          <p className="text-xs text-gray-400 italic">Catatan: {item.notes}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-2 text-center text-gray-600">{item.quantity}</td>
                    <td className="py-2 text-right text-gray-600">{formatCurrency(item.price)}</td>
                    <td className="py-2 text-right font-medium text-gray-900">
                      {formatCurrency(item.subtotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-2 mb-6">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-medium text-gray-900">{formatCurrency(order.subtotal)}</span>
            </div>
            {order.discount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Diskon</span>
                <span className="font-medium text-green-600">
                  -{formatCurrency(order.discount)}
                </span>
              </div>
            )}
            {order.tax > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Pajak</span>
                <span className="font-medium text-gray-900">{formatCurrency(order.tax)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
              <span>Total</span>
              <span className="text-primary-600">{formatCurrency(order.total)}</span>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2">
              {order.paymentMethod === 'cash' ? (
                <span className="text-sm">Tunai</span>
              ) : (
                <CreditCard className="w-4 h-4 text-gray-600" />
              )}
              <span className="text-sm text-gray-600">Pembayaran: {getPaymentMethodLabel(order.paymentMethod)}</span>
            </div>
            {order.paymentMethod === 'cash' && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Uang Diterima</span>
                  <span className="font-medium text-gray-900">{formatCurrency(order.paidAmount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Kembalian</span>
                  <span className="font-medium text-gray-900">{formatCurrency(order.changeAmount)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => navigate({ to: '/app/inventory/orders' })}>
            Kembali
          </Button>
          <Button className="flex-1" onClick={() => setShowReceipt(true)}>
            <Printer className="w-4 h-4 mr-2" />
            Cetak Struk
          </Button>
        </div>
      </main>

      {showReceipt && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowReceipt(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                <h2 className="text-lg font-bold">Struk Pesanan</h2>
                <button
                  onClick={() => setShowReceipt(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <ReceiptContent
                order={order}
                businessName={businessName}
                businessAddress={businessAddress}
                businessPhone={businessPhone}
                width={getSetting<number>('receipt_width') || 58}
              />
              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowReceipt(false)}>
                  Tutup
                </Button>
                <Button className="flex-1" onClick={handlePrint}>
                  <Printer className="w-4 h-4 mr-2" />
                  Cetak
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Clock({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
